import React, { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthRoute, DashboardRoute } from "./RouterHelpers";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import _ from "lodash";
import {
  HOME,
  getPathNames,
  PATIENT,
  STORE,
  SETTINGS,
  CHECKOUT,
  CHAT,
} from "config/helpers/router_helper";
import { motion } from "framer-motion";
import { pageTransition, pageVariants } from "config/helpers/animation_helper";
import { generate_notification, clearTopbarContent } from "redux/actions/AppActions";
import { useDispatch, useSelector } from "react-redux";
import { unixDate } from "config/helpers/date_helper";
import BrowserEventHandler from "components/ui/ConfirmationDialog/BrowserRoutingContoller";
import { ANALYTICS, CALLLOGS, COMPLETED, INSIGHTS, ONBOARDING, SITES, SURVEY, TRIALS } from "config/helpers/router_helper_v2";
import PrivacyPolicyRedirect from "components/privacypolicy/PrivacyPolicyRedirect";
import TermsOfUse from "components/termsofuse/termsOfUse";
import BusinessAssociateAgreement from "components/baa/businessAssociateAgreement";

const USER_AUTHENTICATION = React.lazy(() => import("screens/login/v2/Login"));

const ERROR_PAGE = React.lazy(() => import("components/404/NotFound"));
const TRIAL_PAGE = React.lazy(() =>
  import("screens/login/v2/TrialSubscriptionPage")
);
const NOKI_PLANS = React.lazy(() => import("../../screens/store/Plans"));
const STRIPE_INITIALIZER = React.lazy(() =>
  import("screens/store/stripe/StripeInitializer")
);

const RootRouter = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const unsubscribe = toast.onChange((messages) => {
      if (!messages?.data?.ignoreNotification) {
        dispatch(
          generate_notification({
            message: messages?.content,
            isRead: false,
            addedOn: unixDate(new Date()),
            id: messages?.id,
          })
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [dispatch]);

  useEffect(() => {
    let paths = getPathNames();
    let path = paths[paths.length - 1] || "";

    document.title = `Samaras | ${_.capitalize(
      // path.replaceAll(path?.includes("%20") ? "%20" : "_", " ")
      (path==="pro_agents"||path==="happiness_survey"||path==="outbound_call_analytics")?path?.replaceAll("_"," "):decodeURIComponent(path)
    )}`;

    // Clear topbar content when navigating away from sites
    if (!location.pathname.startsWith('/sites')) {
      dispatch(clearTopbarContent());
    }
  }, [location, dispatch]);

  const hasChildObject = (obj) => {
    let child = [];
    Object.keys(obj).map((m) => {
      if (typeof obj[m] === "object" && !obj[m].hasOwnProperty("$$typeof")) {
        Object.keys(obj[m]).map((p) => {
          child.push({ component: obj[m][p], parent: m, name: p });
          return true;
        });
      }
      return true;
    });
    return child;
  };

  const AnimationExtendedComponent = (props) => {
    let Module = props.jsx;
    let ModuleWrapper = props.wrapper;
    let ChildModuleWrapper = props.childwrapper;
    return (
      <>
        <ModuleWrapper />
        <motion.div
          key={props.componentKey}
          initial="initial"
          animate="in"
          variants={pageVariants}
          transition={pageTransition}
        >
          {(ChildModuleWrapper && <ChildModuleWrapper />) || null}
          <Module />
        </motion.div>
      </>
    );
  };

  const EmptyWrapper = () => {
    return <></>;
  };

  const getParentWrapper = (mod, page = false) => {
    if (page === false) {
      if (
        COMMON_WRAPPERS[mod] &&
        COMMON_WRAPPERS[mod].hasOwnProperty("WRAPPER")
      ) {
        return COMMON_WRAPPERS[mod].WRAPPER;
      } else return EmptyWrapper;
    } else {
      if (
        COMMON_WRAPPERS[mod] &&
        COMMON_WRAPPERS[mod][page] &&
        COMMON_WRAPPERS[mod][page].hasOwnProperty("WRAPPER")
      ) {
        return COMMON_WRAPPERS[mod][page].WRAPPER;
      } else return EmptyWrapper;
    }
  };

  const makePath = (mod, page, sub = false) => {
    let path = "";
    path += _.lowerCase(mod).replaceAll(" ", "_") + "/";

    if (page?.split("_")?.includes("EDIT")) {
      let newUrl = page
        ?.split("_")
        ?.filter((item) => item !== "EDIT")
        .join("_");
      path += _.lowerCase(newUrl).replaceAll(" ", "_") + "/";
      path += ":id";
      return path;
    }

    path += _.lowerCase(page).replaceAll(" ", "_") + "/";
    if (sub) {
      path += _.lowerCase(sub).replaceAll(" ", "_");
    }
    return path;
  };

  const handleBeforeUnload = () => {
    return "Are you sure you want to leave this page?";
  };

  return (
    <>
      <ToastContainerComponent newestOnTop />
      {/* <BrowserEventHandler
        onBeforeUnload={handleBeforeUnload}
      // onUnload={handleUnload}
      /> */}
      <Routes>
        {/* {/ auth routes /} */}
        {Object.keys(MODULES).map((mod, idx) => {
          return (
            <React.Fragment key={`${mod}_${idx}`}>
              {Object.keys(MODULES[mod]).map((page, pId) => {
                let childModules = hasChildObject(MODULES[mod][page]);
                let ParentWrapper = getParentWrapper(mod);
                return (
                  <>
                    {childModules.length === 0 ? (
                      <>
                        {Object.keys(MODULES[mod][page]).map((sub, subId) => {
                          let ChildWrapper = getParentWrapper(mod, page);
                          return (
                            <>
                              {hasChildObject(MODULES[mod][page][sub])
                                .length !== 0 ? (
                                <Route
                                  key={`${page}_${idx}_${pId}_${subId}`}
                                  path={makePath(mod, page, sub)}
                                  index
                                  element={
                                    <DashboardRoute
                                      component={
                                        <AnimationExtendedComponent
                                          componentKey={`${mod}/${page}`}
                                          jsx={MODULES[mod][page][sub]}
                                          wrapper={ParentWrapper}
                                          childwrapper={ChildWrapper}
                                        />
                                      }
                                    />
                                  }
                                />
                              ) : (
                                <></>
                              )}
                            </>
                          );
                        })}
                      </>
                    ) : (
                      <></>
                    )}
                    <Route
                      key={`${page}_${idx}_${pId}`}
                      path={makePath(mod, page)}
                      index
                      element={
                        <DashboardRoute
                          component={
                            <AnimationExtendedComponent
                              componentKey={`${mod}/${page}`}
                              jsx={MODULES[mod][page]}
                              wrapper={ParentWrapper}
                            />
                          }
                        />
                      }
                    />
                  </>
                );
              })}
            </React.Fragment>
          );
        })}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path="/login"
          index
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        {/* <Route
          path="/signup"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/verify/:code"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/reset-password/:code"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/signup/confirm"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />

        <Route
          path="/fail-verify"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/fail-link-used"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route path="/plans" element={<NOKI_PLANS />} />
        <Route path="/checkout" element={<STRIPE_INITIALIZER />} />

        <Route
          path="/reset"
          element={<AuthRoute component={USER_AUTHENTICATION} />}
        />
        <Route
          path="/privacypolicy"
          //element={<AuthRoute component={PRIVACY_POLICY} />}
          element={<PrivacyPolicyRedirect />}
        />
        <Route
          path="/terms-of-use"
          //element={<AuthRoute component={TERMS_OF_USE} />}
          element={<TermsOfUse />}
        />
        <Route
          path="/business-associate-agreement"
          //element={<AuthRoute component={BUSINESS_ASSOCIATE_AGREEMENT} />}
          element={<BusinessAssociateAgreement />}
        />
        <Route path="/welcome" element={<TRIAL_PAGE />} /> */}
        {/*<Route path="/demo" element={<RTCDemo />} />
        <Route path="/demo1" element={<CoquiTest />} />
        <Route path="/demo2" element={<MultiAgentDemo />} />
        <Route path="/demo2/agent" element={<AgentPage />} />*/}
        <Route path="*" element={<ERROR_PAGE />} />
      </Routes>
    </>
  );
};

const COMMON_WRAPPERS = {};

const MODULES = {
  // [HOME]: {
  //   DASHBOARD: React.lazy(() => import("screens/home/Dashboard")),
  //   PRO_AGENTS: React.lazy(() => import("screens/home/proAgents/ProAgents")),
  // },
  // [CHAT]: {
  //   HISTORY: React.lazy(() => import("screens/chatHistory/ChatHistory")),
  //   WINDOW_EDIT: React.lazy(() => import("screens/chatWindow/chatWindow")),
  // },


  // [INSIGHTS]: {

  //   INFO: React.lazy(() => import("screens/home/Dashboard_v2")),

  // },
  // [PATIENT]: {

  //   DASHBOARD_EDIT: React.lazy(() =>
  //     import("screens/patients_v2/dashboard/PatientDashboard")
  //   ),
  //   LIST: React.lazy(() => import("screens/patients_v2/FhirPatientList")),
  //   VISITS_EDIT: React.lazy(() =>
  //     import("screens/patients_v2/clinicalVisits/ClinicalVisits")
  //   ),
  // },


  [SETTINGS]: {
    // ME: React.lazy(() => import("screens/settings/SettingsPage_v2")),
    // ME: React.lazy(() => import("screens/settings/trials/Settings")),
    SCHEDULES: React.lazy(() => import("screens/settings/trials/Settings")),
    CONFIGURATIONS: React.lazy(() => import("screens/settings/trials/Configurations")),
  },
  // [SURVEY]: {
  //   HAPPINESS_SURVEY: React.lazy(() => import("screens/surveys/HappinessSurvey")),
  //   HAPPINESS_FORM_EDIT: React.lazy(() => import("screens/surveys/HappinessFormRenderer")),
  // },
  // [STORE]: {
  //   PLANS: NOKI_PLANS,
  //   CHECKOUT: STRIPE_INITIALIZER,
  // },
  // [CHECKOUT]: {
  //   RETURN: React.lazy(() => import("screens/store/PaymentStatus")),
  // },
  [TRIALS]: {
    PATIENT_EDIT: React.lazy(() => import("screens/clinicalTrials/trialsPatient/TrialsPatientDetails")),
  },
  [CALLLOGS]:{
    LIST:React.lazy(() => import("screens/clinicalTrials/ClinicalTrialsCallLogs")),
    LOG_DETAILS_EDIT:React.lazy(() => import("screens/clinicalTrials/ClinicalTrialsCallLogDetails")),
  },
  [ANALYTICS]:{
    // OUTBOUND_CALL_ANALYTICS: React.lazy(() => import("screens/outboundCallAnalytics/OutboundCallAnalytics")),
    OUTBOUND_CALL_ANALYTICS: React.lazy(() => import("screens/outboundCallAnalytics/VapiCallAnalytics/VapiCallAnalytics")),
  },
  [COMPLETED]:{
    LIST: React.lazy(() => import("screens/clinicalTrials/ClinicalTrialsCompletedCallLogs")),
    LOG_DETAILS_EDIT:React.lazy(() => import("screens/clinicalTrials/completedLogs/ClinicalTrialsCompletedCallLogDetails")),
  },
  [SITES]:{
    LIST: React.lazy(() => import("screens/sites/Sites")),
    // SITE_EDIT: React.lazy(() => import("screens/sites/SingleSite")),
    OUTREACH: React.lazy(() => import("screens/sites/SingleSite")),
    // ONBOARDED: React.lazy(() => import("screens/onboarding/OnboardingList")),
    ONBOARDED: React.lazy(() => import("screens/sites/siteTabs/tabComponents/OnboardedList")),
    ONBOARDED_DETAILS_EDIT: React.lazy(() => import("screens/sites/siteTabs/tabComponents/IntakePreviewForm")),
  },
  // [ONBOARDING]:{
  //   LIST: React.lazy(() => import("screens/onboarding/OnboardingList")),
  //   // ONBOARDING_EDIT: React.lazy(() => import("screens/onboarding/OnboardingDetails")),
  // },
};

export default RootRouter;

const ToastContainerComponent = (props) => {
  const theme = useSelector((state) => state?.app?.theme);

  return <ToastContainer theme={theme} {...props} />;
};
